import { NavLink } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { Avatar } from '../ui/Avatar';
import styles from './Sidebar.module.css';

const NAV_LINK_CLASS = ({ isActive }: { isActive: boolean }): string =>
  isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem;

export function Sidebar(): React.JSX.Element {
  const { user, signOut } = useAuth();
  const isAdmin = user?.roles.includes('Admin') ?? false;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <span className={styles.mark} aria-hidden="true">
          <BracketIcon />
        </span>
        <span>
          <span className={styles.brandName}>ET-HUB</span>
          <span className={styles.brandSub}>EXIGO TECH</span>
        </span>
      </div>

      <nav className={styles.nav} aria-label="Primary">
        <NavLink to="/" end className={NAV_LINK_CLASS}>
          <GridIcon />
          Dashboard
        </NavLink>

        <p className={styles.sectionLabel}>Modules</p>
        <NavLink to="/scenarios" className={NAV_LINK_CLASS}>
          <DocIcon />
          MCI Engagement Finder
        </NavLink>

        {isAdmin && (
          <NavLink to="/admin" className={NAV_LINK_CLASS}>
            <GearIcon />
            Administration
          </NavLink>
        )}

        <div className={styles.comingSoon}>
          <p className={styles.comingSoonTitle}>Coming soon</p>
          <p className={styles.comingSoonBody}>More modules in development</p>
        </div>
      </nav>

      <div className={styles.userBlock}>
        <Avatar name={user?.displayName ?? user?.upn ?? 'User'} size="md" />
        <div className={styles.userText}>
          <p className={styles.userName}>{user?.displayName ?? 'Signed in'}</p>
          <p className={styles.userEmail}>{user?.upn}</p>
        </div>
        <button type="button" className={styles.signOut} onClick={signOut} aria-label="Sign out">
          <SignOutIcon />
        </button>
      </div>
    </aside>
  );
}

function BracketIcon(): React.JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 4L3 12L8 20M16 4L21 12L16 20"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GridIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="9" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function DocIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 1.5H9.5L13 5V13.5A1 1 0 0 1 12 14.5H4A1 1 0 0 1 3 13.5V2.5A1 1 0 0 1 4 1.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function GearIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 1.8V3.2M8 12.8V14.2M14.2 8H12.8M3.2 8H1.8M12.2 3.8L11.2 4.8M4.8 11.2L3.8 12.2M12.2 12.2L11.2 11.2M4.8 4.8L3.8 3.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SignOutIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M6 14H3.5A1.5 1.5 0 0 1 2 12.5V3.5A1.5 1.5 0 0 1 3.5 2H6M10.5 11.5L14 8L10.5 4.5M14 8H6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
